import { createSelector } from 'reselect';
import { itemsFacetMetadataSelector } from './items';
import { isFilterApplied, popFilter, appendFilter, decodeFilters, applyFilter } from 'Helpers/filters';
import { availableFiltersAdapter, filtersAppliedAdapter } from 'Helpers/filtersAdapter';
import { categorySelector } from './categories';
import flatten from 'lodash/flatten';

// filter selectors

const radioTypes = ['furnished', 'brokerfree', 'pets', 'ft_unit'];

export const getFilterType = filter => {
    if (radioTypes.includes(filter.id)) {
        return 'radio';
    }

    if (filter.type === 'nested_select') {
        return 'nestedCheckbox';
    }

    return filter.type;
};

const filtersToShow = ({ id, isChild } = {}) => {
    if (isChild || id === 'category') {
        return false;
    }
    return true;
};

export const getAvailableFilters = createSelector(
    (state, props) => {
        return props.type === 'landingPage' ? state.filtersAbundance.data : itemsFacetMetadataSelector(state, props);
    },
    itemsMetadata => {
        const facetsFilters = (itemsMetadata && itemsMetadata.filters) || [];

        return availableFiltersAdapter(facetsFilters);
    }
);

/* getAppliedFilters */
export const getAppliedFilters = createSelector(
    itemsFacetMetadataSelector,
    itemsMetadata => {
        const filters = (itemsMetadata && itemsMetadata.applied_filters) || [];
        const facetsFilters = filters.filter(filter => filter.id !== 'category' && filter.id !== 'location');

        return filtersAppliedAdapter(facetsFilters);
    }
);

export const getAppliedFilterCategoryId = createSelector(
    itemsFacetMetadataSelector,
    itemsMetadata => {
        const filters = (itemsMetadata && itemsMetadata.applied_filters) || [];
        const [categoryFilter] = filters.filter(filter => filter.id === 'category');
        const category = categoryFilter && categoryFilter.values[0];

        return category && category.id;
    }
);

/** Returns all the categories in an object which keys are the category key
 * @param {Object} state - redux's state
 * @param {Object} props - { params: { categoryID } }
 * @return {Object} - Categories indexed by key
 */
export const categoryFiltersByKeySelector = createSelector(
    (state, { params: { categoryID }}) => (categorySelector(state, categoryID) || {}).params,
    (filters = []) => filters.reduce((filters, filter) => ({
        ...filters,
        [filter.key]: filter
    }), {})
);

export const nestedFiltersKeysSelector = createSelector(
    categoryFiltersByKeySelector,
    filters => {
        const reducerFn = (prev, curr) => {
            if (curr.children) {
                prev = {
                    ...prev,
                    [curr.key]: curr.children.map(child => child.key),
                    ...curr.children.reduce(reducerFn, prev)
                };
            }

            return prev;
        };

        const result = Object
            .values(filters)
            .reduce(reducerFn, {});

        return result;
    }
);

const nestedFiltersValuesSelector = createSelector(
    nestedFiltersKeysSelector,
    (nestedFilters = {}) => {
        const nestedFiltersValues = (nestedFilters && Object.values(nestedFilters)) || [];

        return flatten(nestedFiltersValues);
    }
);

const hasDefinitionSearch = option => option
    && option.definitions
    && typeof option.definitions.search !== 'undefined'
    && typeof option.definitions.search.type !== 'undefined';

const getFilterChildren = (filters, children) => {
    return filters.reduce((prev, curr) => {
        const childrenKey = children ? children.key : null;

        if (curr.id === childrenKey) {
            prev = {
                ...prev,
                ...{
                    ...curr,
                    ...{
                        children: children.children
                            ? getFilterChildren(filters, children.children[0])
                            : null
                    }
                }
            };
        }

        return prev;
    }, {});
};

export const getFilters = createSelector(
    getAvailableFilters,
    categoryFiltersByKeySelector,
    nestedFiltersValuesSelector,
    (filters, filtersByKey, nestedFiltersValues) => filters
        .map(f => {
            const filter = {
                ...f,
                type: getFilterType(f)
            };
            const filterFromCategory = filtersByKey[filter.id];

            filter.display_order = hasDefinitionSearch(filterFromCategory) ? filterFromCategory.definitions.search.display_order : -1;
            if (!filterFromCategory || !filterFromCategory.children || !filterFromCategory.children[0]) {
                if (nestedFiltersValues.includes(filter.id)) {
                    return {
                        ...filter,
                        isChild: true
                    };
                }
                return filter;
            }

            const children = getFilterChildren(filters, filterFromCategory.children[0]);

            if (!children) {
                return filter;
            }

            return {
                ...filter,
                children
            };
        })
        .sort((a, b) => a.display_order - b.display_order)
);

export const appliedFiltersByURL = params => decodeFilters(params.filter);

export const getFiltersToShow = (state, props) =>
    getFilters(state, props).filter(filtersToShow);

export const getNestedAndSelectFilters = createSelector(
    getFiltersToShow,
    filtersToShow => filtersToShow.filter(filter => ['nestedCheckbox', 'select'].includes(filter.type) && filter.values.length)
);

export const getAppliedFilter = (state, { filterId }) =>
    state.track.filters[filterId] || '';

/** Builds the selector for (nested) Checkbox (group) filter.
 *
 * @param {Function} buildFilters - (applied, appliedFilters, id, value.key) =>
 * @return {Selector}
 */
const checkboxValuesSelectorBuilder = buildFilters =>
    createSelector(
        ({ filter: { values }}) => values,
        ({ filter: { id }}) => id,
        ({ appliedFilters }) => appliedFilters,
        ({ buildURL }) => buildURL,
        ({ filter: { children }}) => children,
        (values, id, appliedFilters, buildURL, nestedFilters) =>
            values
                .map(value => {
                    const key = value.key || value.id;
                    const applied = !!isFilterApplied(appliedFilters, id, key);
                    const excludedNestedFilters = nestedFilters && nestedFilters.id;
                    const filters = buildFilters(applied, appliedFilters, id, key, [excludedNestedFilters]);
                    const to = buildURL(filters);

                    return {
                        ...value,
                        to,
                        applied
                    };
                })
                .sort((a, b) => {
                    if (!!a.applied !== !!b.applied) {
                        return a.applied ? -1 : 1;
                    }

                    return (parseInt(b.count, 10) - parseInt(a.count, 10)) || (parseInt(a.display_order, 10) - parseInt(b.display_order, 10));
                })
    );

/** Get values for a CheckboxGroup filter.
 *
 * @param {Props} - {
 *   {Array} values:
 *   {String} id:
 *   {Object} appliedFilters: retrieved from `decodeFilters`
 *   {Function} buildURL: (Object : filtersObj) -> String : url
 * }
 * @return {Array} Values
 */
export const checkboxGroupValuesSelector = checkboxValuesSelectorBuilder(
    (applied, appliedFilters, id, key, excludedNestedFilters) => applied // eslint-disable-line no-confusing-arrow
        ? popFilter(appliedFilters, id, key, excludedNestedFilters)
        : appendFilter(appliedFilters, id, key)
);

/** Get values for a NestedCheckbox filter.
 *
 * @param {Props} - {
 *   {Array} values:
 *   {String} id:
 *   {Object} appliedFilters: retrieved from `decodeFilters`
 *   {Function} buildURL: (Object : filtersObj) -> String : url
 * }
 * @return {Array} Values
 */
export const nestedCheckboxValuesSelector = checkboxValuesSelectorBuilder(
    (applied, appliedFilters, id, key, excludedNestedFilters) => applied // eslint-disable-line no-confusing-arrow
        ? popFilter(appliedFilters, id, key, excludedNestedFilters)
        : applyFilter(appliedFilters, { [id]: key }, undefined, excludedNestedFilters)
);

/**
 * State selectors for makeGetCategoryWithMetadata
 */
export const getElement = ({ categories }, { categoryID }) => categories.elements && categories.elements[categoryID];
export const getMetadata = ({ categories }, { categoryID }) => (
    categories.metadataSearch && categories.metadataSearch.data && categories.metadataSearch.data[categoryID]
);

/**
 * - Merges the selected category element with the corresponding metadata
 *   from the metadata search endpoint.
 * - Adds a "withMetadata" flag to the current category element so that
 *   we know it has already been processed and avoids the recomputing.
 * @param {Object} elements - state.categories.elements
 * @param {Object} metadata - state.categories.metadataSearch with selected categoryId
 */
export const makeGetCategoryWithMetadata = () => createSelector([
    getElement,
    getMetadata
], (element, metadata) => {
    if ((element && !element.params) || !metadata) {
        return null;
    }

    const checkSearchDefinition = elm => elm.definitions && elm.definitions.search && Object.keys(elm.definitions.search).length;
    const getRenderAs = param => checkSearchDefinition(param) && param.definitions.search.render_as;
    const getRenderKey = param => checkSearchDefinition(param) && param.definitions.search.key;
    const buttonFixedMerger = params => ({
        ...params,
        ...metadata[getRenderAs(params)][getRenderKey(params)]
    });
    const sliderMerger = param => {
        const renderAs = getRenderAs(param);
        const renderKey = getRenderKey(param);
        let newRange;

        if (param.range && param.range.length) {
            newRange = param.range && param.range.map(elm => {
                return {
                    ...elm,
                    ...metadata[renderAs][renderKey],
                    min_value: metadata[renderAs][renderKey].min,
                    max_value: metadata[renderAs][renderKey].max
                };
            });
        }
        else {
            newRange = [
                {
                    id: 'min',
                    ...metadata[renderAs][renderKey],
                    min_value: metadata[renderAs][renderKey].min,
                    max_value: metadata[renderAs][renderKey].max
                },
                {
                    id: 'max',
                    ...metadata[renderAs][renderKey],
                    min_value: metadata[renderAs][renderKey].min,
                    max_value: metadata[renderAs][renderKey].max
                }
            ];
        }
        return { ...param, range: [...newRange], rangeMetadata: true };
    };
    const mergers = param => {
        if (!metadata[getRenderAs(param)] || !metadata[getRenderAs(param)][getRenderKey(param)]) {
            return param;
        }

        switch (getRenderAs(param)) {
            case 'slider':
                return sliderMerger(param);
            case 'button_fixed':
            case 'button_carousel':
                return buttonFixedMerger(param);
            default:
                return param;
        }
    };
    const newParams = element.params.map(param => mergers(param));

    return { ...element, params: newParams, withMetadata: true };
});
